/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import http from "../../../http-common";
import { useSocket } from "../../../context/SocketProvider";
import { ChatState } from "../../../context/ChatProvider";
import { NotificationContext } from "../../../context/NotificationContext";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faChevronLeft } from "@fortawesome/free-solid-svg-icons";

let selectedChatCompare;
export default function MessageBox({ fetchAgain, setFetchAgain, onBackToChatList }) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const socket = useSocket();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const { selectedChat, setSelectedChat, notification, setNotification } = ChatState();
  const { incrementMessage, setUserInfo } = useContext(NotificationContext);

  const isLoggedUser = (sender) => sender._id === user.id;

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", { hour12: false, timeStyle: "short" });
  };

  const fetchMessages = async () => {
    if (!selectedChat) return;
    try {
      const { data } = await http.get(`/message/${selectedChat._id}`);
      setMessages(data);
      socket?.emit("join chat", selectedChat._id);
    } catch (error) {
      console.log(`error: ${error.message}`);
    }
  };

  const typingHandler = (e) => setNewMessage(e.target.value);

  const sendMessage = async () => {
    if (!newMessage?.trim()) return;
    try {
      setNewMessage("");
      const { data } = await http.post("/message", {
        chatId: selectedChat._id,
        content: newMessage,
      });
      socket?.emit("new message", data);
      setMessages([...messages, data]);
      incrementMessage();
      const recipient = selectedChat.users.find((u) => u._id !== user.id);
      if (recipient?._id) setUserInfo(recipient._id);
    } catch (error) {
      console.log(`error: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchMessages();
    selectedChatCompare = selectedChat;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat]);

  useEffect(() => {
    // Uses the shared, persistent socket (see SocketProvider) rather than a
    // throwaway one created per mount, so the listener must be explicitly
    // registered/cleaned up here instead of relying on socket.disconnect() to
    // implicitly drop it -- otherwise it stacks a duplicate handler on every
    // re-render. selectedChatCompare/markAsRead read live values instead of
    // being added as deps, so this only needs to run once per socket instance.
    if (!socket) return;
    const handleMessageReceived = (newMessageRecieved) => {
      if (!selectedChatCompare || selectedChatCompare._id !== newMessageRecieved.chat._id) {
        setNotification((prev) =>
          prev.includes(newMessageRecieved) ? prev : [newMessageRecieved, ...prev]
        );
      } else {
        setMessages((prev) => [...prev, newMessageRecieved]);
        markAsRead();
      }
    };
    socket.on("message recieved", handleMessageReceived);
    return () => socket.off("message recieved", handleMessageReceived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // Reads selectedChatCompare (always current, see the effect above), not the
  // selectedChat closed over by the socket listener, which is only registered
  // once and would otherwise keep pointing at whichever chat was selected when
  // the listener was first attached.
  const markAsRead = async () => {
    try {
      await http.put(`/message/updateMessage/${selectedChatCompare._id}`, {});
    } catch (error) {
      console.log(`message: ${error.message}`);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  if (!selectedChat) {
    return (
      <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
        <p className="tw-text-white/30 tw-text-sm">{t("Select a conversation to display")}</p>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-w-full">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-border-b tw-border-white/10 tw-flex-shrink-0">
        <button
          onClick={onBackToChatList}
          className="tw-flex md:tw-hidden tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-white/10 hover:tw-bg-white/20 tw-text-white tw-border-0 tw-cursor-pointer"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="tw-text-xs" />
        </button>
        <p className="tw-text-sm tw-font-semibold tw-text-white tw-m-0">
          {selectedChat.chatName}
        </p>
      </div>

      {/* Messages */}
      <div className="tw-flex-1 tw-overflow-auto tw-p-4 tw-flex tw-flex-col tw-gap-3">
        {messages?.map((message) => {
          const mine = isLoggedUser(message.sender);
          const avatar = message.sender.picture?.startsWith("https")
            ? message.sender.picture
            : `${process.env.REACT_APP_AWS_URL}/${message.sender.picture}`;

          return (
            <div key={message._id} className={`tw-flex tw-items-end tw-gap-2 ${mine ? "tw-flex-row-reverse" : ""}`}>
              <div className="tw-relative tw-flex-shrink-0">
                <img
                  className="tw-h-8 tw-w-8 tw-rounded-lg tw-object-cover"
                  src={avatar}
                  alt="profile"
                />
              </div>
              <div className={`tw-flex tw-flex-col tw-gap-1 ${mine ? "tw-items-end" : "tw-items-start"}`}>
                <span className="tw-text-xs tw-text-white/30">{formatTimestamp(message.createdAt)}</span>
                <span
                  className={`tw-max-w-xs lg:tw-max-w-md tw-rounded-2xl tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-break-words ${
                    mine ? "tw-bg-[#712CB0] tw-rounded-br-sm" : "tw-bg-white/10 tw-rounded-bl-sm"
                  }`}
                >
                  {message.content}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-border-t tw-border-white/10 tw-flex-shrink-0">
        <input
          value={newMessage}
          onChange={typingHandler}
          onKeyDown={handleKeyDown}
          className="tw-flex-1 tw-rounded-xl tw-border tw-border-white/20 tw-bg-[#190033] tw-px-4 tw-py-2.5 tw-text-sm tw-text-white placeholder:tw-text-white/30 focus:tw-border-[#712CB0] focus:tw-outline-none tw-transition-colors"
          placeholder={t("Your message...")}
        />
        <button
          onClick={sendMessage}
          className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#712CB0] hover:tw-bg-[#8B3FD9] tw-text-white tw-transition-all tw-border-0 tw-cursor-pointer tw-flex-shrink-0"
        >
          <FontAwesomeIcon icon={faPaperPlane} className="tw-text-xs" />
        </button>
      </div>
    </div>
  );
}
